import { useState } from 'react'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/cn'
import { useRegisterPolicy, useRegister, RegisterApiError } from '@/lib/auth/register-api'
import { SignupStepper } from './SignupStepper'
import { SignupStep1 } from './SignupStep1'
import { SignupStep2 } from './SignupStep2'
import { ControlPlaneArtwork } from './ControlPlaneArtwork'
import { AccountCreatedScreen, accountCreatedNextCopy } from './AccountCreatedScreen'
import type { Step1Data, Step2Data, OrgType } from './signup-types'

/** Right-panel page heading (PRD AC11) — the page's only <h1>. Swaps per step like the hero copy. */
function getPageHeading(step: SignupFlow): string {
  if (step === 'submitted') return 'Verify your email'
  if (step === 2) return 'Set up your workspace'
  return 'Create your account'
}

type SignupFlow = 1 | 2 | 'submitted'

interface LeftPanelCopy {
  headline: string
  body: string
  detail?: string
}

function getLeftCopy(step: SignupFlow, orgType: OrgType): LeftPanelCopy {
  if (step === 'submitted') {
    return {
      headline: 'Almost there.',
      body: accountCreatedNextCopy(orgType),
    }
  }
  if (step === 2) {
    return {
      headline: 'Tell us about your organisation.',
      body: 'EntityOS adapts to how your team works — whether you manage your own entities or run a multi-client practice.',
      detail: 'This takes 60 seconds.',
    }
  }
  return {
    headline: 'Institutional-grade governance. Built for legal teams.',
    body: 'Manage legal entities, ownership structures, and compliance obligations — all in one place built for the complexity of corporate legal work.',
    detail: 'Join teams at law firms, fund administrators, and in-house legal departments.',
  }
}

/**
 * Page shell for the public /signup route.
 * Owns the three-step flow: Step 1 (identity) → Step 2 (workspace + fork) → AccountCreatedScreen.
 * Rendered inside SignupApp (standalone, no Keycloak).
 */
export function SignupPage() {
  const [flow, setFlow] = useState<SignupFlow>(1)
  const [orgType, setOrgType] = useState<OrgType>('client')
  const [step1Data, setStep1Data] = useState<Step1Data | null>(null)
  const [step2Draft, setStep2Draft] = useState<Partial<Step2Data> | null>(null)
  const [submittedEmail, setSubmittedEmail] = useState<string>('')
  const [globalError, setGlobalError] = useState<string | null>(null)

  const { data: policy, isLoading: policyLoading, error: policyError } = useRegisterPolicy()
  const register = useRegister()

  const leftCopy = getLeftCopy(flow, orgType)

  async function handleStep2Next(step2Data: Step2Data) {
    if (!policy || !step1Data) return
    setGlobalError(null)
    setOrgType(step2Data.orgType)

    try {
      await register.mutateAsync({
        email: step1Data.email,
        password: step1Data.password,
        pocName: step1Data.pocName,
        companyName: step2Data.companyName,
        website: step2Data.website || undefined,
        countryCode: step2Data.countryCode,
        acceptedTermsVersion: policy.termsVersion,
        // Internal OrgType uses lowercase; wire contract uses uppercase to match BE persona vocab
        orgType: step2Data.orgType.toUpperCase() as 'CLIENT' | 'AGENCY',
      })
      setSubmittedEmail(step1Data.email)
      setFlow('submitted')
    } catch (err) {
      if (err instanceof RegisterApiError) {
        if (err.code === 'rate_limited') {
          setGlobalError('Too many registration attempts. Please wait a moment and try again.')
        } else if (err.code === 'unavailable') {
          setGlobalError('Registration is temporarily unavailable. Please try again shortly.')
        } else if (err.code === 'field_validation' && err.fieldErrors?.length) {
          setGlobalError(
            `Please check your details: ${err.fieldErrors.map((f) => f.message).join(', ')}`,
          )
        } else {
          setGlobalError('Something went wrong. Please try again.')
        }
      } else {
        setGlobalError('Something went wrong. Please try again.')
      }
    }
  }

  const currentStepNumber: 1 | 2 | 3 = flow === 'submitted' ? 3 : (flow as 1 | 2)

  return (
    <div className="min-h-screen bg-bg text-text flex flex-col min-[961px]:flex-row">
      {/* Left hero panel — always visible: a ~92px masthead band below 960px (PRD AC6, mirroring
          entite.css:311-332), the full ink hero above it. --ink is deliberately not swapped via a
          Tailwind breakpoint alias like `lg`; PRD AC6 requires the exact 960px value verbatim. */}
      <div
        className={cn(
          'signup-hero-lit-ground flex flex-col bg-ink overflow-hidden shrink-0',
          'px-6 py-5',
          // Collapsed-band height, exact: entite.css:311 pins `min-height: 92px` on the masthead
          // band, not "approximately" — measured sign-in at 92.0px at 960/768/375. Without this,
          // the band is content-sized and lands ~6px short (86px). min-[961px]:min-h-screen below
          // overrides this back to full-height above the breakpoint.
          'min-h-[92px]',
          // Vertical alignment: entite.css:311's collapsed masthead sets `align-items: center` to
          // centre the wordmark within the 92px band (its footer/artwork/copy siblings are all
          // display:none there, so there is nothing to justify-between against). Above 961px, the
          // wordmark block and the footer ARE both visible again, so justify-between correctly
          // pushes them to the panel's top/bottom as before.
          'justify-center min-[961px]:justify-between',
          // 42% width, flat (no xl step-up) — matches the reference's own
          // `grid-template-columns: minmax(0, 42fr) minmax(0, 58fr)` (entite.css:71), which never
          // widens the ratio above any breakpoint either. At 1440 this renders 605px, the measured
          // sign-in target — proportional, not a hard-coded px width, per instruction.
          'min-[961px]:min-h-screen min-[961px]:w-[42%]',
          // px-13 = 52px, flat — matches entite.css:90's own fixed `padding: 3.1rem 3.25rem`
          // (3.25rem horizontal = 52px), which likewise never steps up at a wider breakpoint.
          'min-[961px]:px-13 min-[961px]:py-10',
        )}
        data-testid="signup-left-panel"
      >
        <div>
          {/* Wordmark + tagline — the masthead. Visible at every width per AC6 ("wordmark (and
              subline) only" survives the collapse). Brand mark, not a section heading — no heading
              of any level precedes the page's one <h1>, which lives in the right panel (PRD rev 5
              AC11). Always the DARK wordmark variant, unconditionally — this panel is always on an
              inverse/dark background regardless of the app's own light/dark theme (contrast
              Sidebar.tsx, whose wordmark IS theme-reactive via useDataTheme). */}
          <div className="mb-0 min-[961px]:mb-10">
            {/* `.signup-hero-wordmark-img` (index.css) reproduces the same 960px breakpoint and the
                same pinned/clamp() values the old text-based `.signup-hero-wordmark` class used
                (entite.css:190's formula), but on `height` — font-size/line-height/letter-spacing do
                nothing on an <img>. width:auto preserves the source 720:282 aspect ratio. width/
                height attrs below are the source asset's real pixel dimensions (720×282) — they set
                the browser's intrinsic aspect-ratio hint for CLS purposes; the CSS rule still governs
                the actual rendered size (MR !435 review). */}
            <img
              src="/brand/entiti-wordmark-dark.png"
              alt="Entiti"
              width={720}
              height={282}
              className="signup-hero-wordmark-img"
            />
            {/* Same mechanism and reasoning as the wordmark, via `.signup-hero-tagline`: matches
                entite.css:195's clamp() above 960px, pins 12px in the collapsed band below it.
                `body-sm`'s weight (400) and lack of letter-spacing already match sign-in's "0".
                `inverse-subtle` (Text.tsx) — a named, reused 60%-opacity tier — not an ad-hoc
                `opacity-60` className (MR review: five near-identical opacity literals duplicated
                across this file and ControlPlaneArtwork.tsx, unlabeled). */}
            <Text variant="body-sm" color="inverse-subtle" className="signup-hero-tagline mt-1">
              Corporate governance platform
            </Text>
          </div>

          {/* Hairline masthead rule — border-white/[0.11], not a second CSS custom property (it's
              white at reduced opacity, not a new hue; see DD-signup-match-signin-visual.md). Hidden
              below 960px along with everything else the collapsed band omits. */}
          <div className="hidden min-[961px]:block border-t border-white/[0.11] mt-6 mb-8" />

          {/* Dynamic copy — headline/body/detail continue to swap per step via getLeftCopy(); the
              panel/wordmark/rule/artwork/stat-row stay constant (PRD §5.2). Hidden below 960px:
              AC6 requires display:none here, not a smaller size. */}
          <div key={String(flow)} className="hidden min-[961px]:block">
            {/* Non-semantic by design, not an oversight: the reference paints this via a CSS
                pseudo-element (entite.css #kc-header-wrapper::before), so sign-in's only real
                heading is its form heading. Explicit as="p" — omitting `as` here would silently
                fall back to headline-md's h2 default (Text.tsx DEFAULT_ELEMENT) and precede the
                page's one <h1>, which is exactly the regression PRD rev 5 AC11 fixes.

                `.signup-hero-headline` (index.css) matches entite.css:207's clamp() formula
                verbatim, viewport-responsive rather than the single 1440px point value (30px) an
                earlier revision pinned — that number was only ever correct at exactly 1440.
                `headline-md`'s weight (700) doesn't match sign-in's (600), so unlike the wordmark
                the class sets weight too; kept the variant only for its font-family (Manrope).
                Confirmed live by the orchestrator: Manrope 600 reads correctly against sign-in's
                system-stack 600 at this size. */}
            <Text as="p" variant="headline-md" color="inverse" className="signup-hero-headline mb-3">
              {leftCopy.headline}
            </Text>
            {/* inverse-muted (70%, Text.tsx) — named tier, not an ad-hoc opacity-70 className. */}
            <Text variant="body-md" color="inverse-muted" className="leading-relaxed mb-4">
              {leftCopy.body}
            </Text>
            {leftCopy.detail && (
              <Text variant="body-sm" color="inverse-subtle">
                {leftCopy.detail}
              </Text>
            )}
          </div>

          {/* Control-plane diagram + stat row — one aria-hidden unit (PRD AC7), fixed across all
              three steps (PRD §5.2), hidden below 960px (PRD AC6). */}
          <ControlPlaneArtwork className="hidden min-[961px]:block mt-8" />
        </div>

        {/* Footer — additive (sign-in has none; PRD doesn't ask for its removal), hidden below
            960px along with the rest of the full hero content. */}
        {/* `inverse-subtle` (Text.tsx, 60% opacity, measures 5.94:1 against --ink) — not the ad-hoc
            `opacity-40` originally drafted (~3.34:1, failed WCAG AA) nor the `opacity-55` (~5.2:1)
            it was first raised to: consolidated onto the same named tier the tagline/detail/stat-
            row labels use, which only ever raises this element's margin, never lowers it.
            `.signup-hero-footer` (index.css), not the pre-existing `text-[9px]` arbitrary className
            it replaces — found during MR self-review: `variant="body-sm"` is `.text-body-sm`
            (unlayered, 12px), so that Tailwind arbitrary utility was ALSO silently losing the
            cascade, the same bug class as the stat row's, and predates this feature entirely (it
            was already on this element before any of this PR's changes). Fixed here since it's the
            exact pattern this PR spent multiple rounds chasing and this line was already touched. */}
        {/* The pre-rebrand spelling was deliberately reverted here on 2026-09-27 (EW-FIX-77)
            pending a dedicated owner ruling on the registered company name, separate from the
            brand-copy rename. That ruling landed 2026-09-29 (PRD
            docs/prd/entiti-brand-logo-rebrand.md §7, "RESOLVED... No line is held back") —
            "Entiti Ltd" is the confirmed legal name. */}
        <Text variant="body-sm" color="inverse-subtle" className="hidden min-[961px]:block signup-hero-footer">
          © {new Date().getFullYear()} Entiti Ltd. All rights reserved.
        </Text>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Page heading (PRD AC11) — the page's only <h1>, swaps per step alongside the hero copy. */}
          <Text as="h1" variant="headline-sm" className="signup-hero-page-heading mb-4">
            {getPageHeading(flow)}
          </Text>

          {/* Stepper stays mounted on the account-created screen (currentStepNumber=3 there).
              Account and Workspace read as done; Verify email reads as the CURRENT step, not a
              third done one — the user hasn't clicked the emailed link yet, and the screen's own
              copy says so. Onboarding itself is not represented in this stepper at all — it has
              its own Day0StepperCard past the auth boundary. */}
          <SignupStepper currentStep={currentStepNumber} />

          {/* Global error */}
          {globalError && (
            <div
              role="alert"
              data-testid="signup-global-error"
              className="mb-4 rounded-[9px] border border-danger-muted bg-danger-subtle px-3 py-2.5"
            >
              <Text variant="body-sm" color="danger">
                {globalError}
              </Text>
            </div>
          )}

          {/* Step rendering */}
          {policy && flow === 1 && (
            <SignupStep1
              policy={policy}
              initialValues={step1Data ?? undefined}
              onNext={(data) => {
                setStep1Data(data)
                setFlow(2)
              }}
            />
          )}

          {policy && flow === 2 && (
            <SignupStep2
              policy={policy}
              initialValues={step2Draft ?? undefined}
              onNext={(data) => void handleStep2Next(data)}
              onBack={(draft) => { setStep2Draft(draft); setFlow(1) }}
              isSubmitting={register.isPending}
            />
          )}

          {flow === 'submitted' && (
            <AccountCreatedScreen email={submittedEmail} orgType={orgType} />
          )}

          {/* Loading / error states for policy fetch */}
          {policyLoading && (
            <div className="flex items-center justify-center py-12" data-testid="signup-policy-loading">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          )}

          {policyError && !policyLoading && (
            <div
              role="alert"
              data-testid="signup-policy-error"
              className="rounded-[9px] border border-danger-muted bg-danger-subtle px-3 py-2.5"
            >
              <Text variant="body-sm" color="danger">
                Unable to load registration form. Please refresh the page.
              </Text>
            </div>
          )}

          {/* Already have an account */}
          {flow !== 'submitted' && (
            <Text variant="body-sm" color="muted" className="text-center mt-6">
              Already have an account?{' '}
              <a href="/login" className="text-primary hover:underline">
                Sign in
              </a>
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
